import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { getUserState } from '../../features/user/selectors';
import SecurityGroupsPage from '../form-pages/security-groups/SecurityGroupsPage';
import NavAppbar from '../nav-top/NavAppbar';
import { ICompany, ISimplifiedProject, ISubscriber } from '../../api-client/autogenerated';
import { getSubscriberById, getSubscriberProjectUserSummary } from '../../models/api/subscribers';
import { getCompanyById } from '../../models/api/companies';
import UsersTable, { UserRow } from './UsersTable';
import CircularLoader from '../loader/CircularLoader';
import { useParams } from 'react-router';
import AdminProjects from './AdminProjects';
import ActionTakenLabelsPanel from './ActionTakenLabelsPanel';
import FolderStructures from './FolderStructures';

const SECTION_IDS = {
  projects: 'projects',
  users: 'users',
  securityGroups: 'security-groups',
  defaultFolders: 'default-folder-structure',
  actionTakenLabels: 'action-taken-labels',
};

/** The cards, top to bottom. Order matters: it is how the current section is worked out. */
const DASHBOARD_SECTIONS = [
  { id: SECTION_IDS.projects, label: 'Projects' },
  { id: SECTION_IDS.users, label: 'Users' },
  { id: SECTION_IDS.securityGroups, label: 'Security Groups' },
  { id: SECTION_IDS.defaultFolders, label: 'Default Folders' },
  { id: SECTION_IDS.actionTakenLabels, label: 'Action Labels' },
];

const LAST_SECTION_ID = DASHBOARD_SECTIONS[DASHBOARD_SECTIONS.length - 1].id;

/** A card becomes the current section once its top is this close to the top of the window. */
const ACTIVE_SECTION_OFFSET = 96;

/**
 * Where a jumped-to card's top lands, measured from the top of the window -- clear of the sticky
 * nav. Scrolled by hand rather than with scroll-margin-top, which JSS drops.
 */
const SECTION_SCROLL_OFFSET = 72;

const useStyles = makeStyles(() =>
  createStyles({
    sectionNav: {
      position: 'sticky',
      top: 0,
      zIndex: 20,
      marginBottom: 16,
      // Opaque, and the same color as the page, so cards pass cleanly underneath.
      backgroundColor: '#FAFAFA',
      borderBottom: '1px solid #EDECEC',
    },
  }),
);

const SubscriberAdminDashboard = () => {
  const classes = useStyles();
  const user = useSelector(getUserState);

  const { subscriberId } = useParams<{ subscriberId: string }>();

  const [isLoading, setIsLoading] = useState(true);

  const [subscriber, setSubscriber] = useState<ISubscriber | null>(null);
  const [company, setCompany] = useState<ICompany | null>(null);
  const [projects, setProjects] = useState<ISimplifiedProject[]>([]);
  const [userRows, setUserRows] = useState<UserRow[]>([]);
  const [activeSection, setActiveSection] = useState(DASHBOARD_SECTIONS[0].id);

  const refreshSubscriber = () => {
    if (!subscriberId) return;

    return getSubscriberById(subscriberId).then(setSubscriber);
  };

  const buildRows = async (subscriberId: string) => {
    const summary = await getSubscriberProjectUserSummary(subscriberId);
    setProjects(summary.projects);
    setUserRows(
      summary.users.map((user) => {
        return {
          id: user.id,
          name: user.name,
          company: user.companyName || 'N/A',
          email: user.email,
          projects: summary.projects
            .filter((p) => user.projectIds.includes(p.id))
            .map((project) => ({ id: project.id, name: project.name })),
          disabled: user.isDisabled,
          isRegistered: user.isRegistered,
        };
      }),
    );
  };

  useEffect(() => {
    refreshSubscriber();
  }, [user]);

  useEffect(() => {
    if (subscriber) {
      setIsLoading(true);
      buildRows(subscriber.id).finally(() => setIsLoading(false));
    }
  }, [subscriber]);

  useEffect(() => {
    if (subscriber?.companyId) {
      getCompanyById(subscriber.companyId).then(setCompany);
    } else {
      setCompany(null);
    }
  }, [subscriber?.companyId]);

  // Follow the scroll position so the nav shows which card is being looked at. The cards only
  // exist once loading finishes, so this waits for them.
  useEffect(() => {
    if (isLoading) return undefined;

    let frame = 0;
    const updateActiveSection = () => {
      frame = 0;
      // The last card is short enough that its top never reaches the offset, so treat the bottom
      // of the page as being in it.
      const isAtBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 4;
      if (isAtBottom) {
        setActiveSection(LAST_SECTION_ID);
        return;
      }
      const current = DASHBOARD_SECTIONS.slice()
        .reverse()
        .find((section) => {
          const element = document.getElementById(section.id);
          return !!element && element.getBoundingClientRect().top <= ACTIVE_SECTION_OFFSET;
        });
      setActiveSection(current?.id || DASHBOARD_SECTIONS[0].id);
    };
    const handleScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isLoading]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - SECTION_SCROLL_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const getContent = () => {
    if (isLoading)
      return (
        <div style={{ height: '80vh', display: 'flex', alignItems: 'center' }}>
          <CircularLoader fullWidth />
        </div>
      );

    if (user.adminOfSubscriberId === subscriberId || user.isSiteAdmin) {
      return (
        <>
          <div className={classes.sectionNav}>
            <Tabs
              value={activeSection}
              onChange={(_event, sectionId) => scrollToSection(sectionId)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {DASHBOARD_SECTIONS.map((section) => (
                <Tab key={section.id} value={section.id} label={section.label} />
              ))}
            </Tabs>
          </div>
          <section id={SECTION_IDS.projects}>
            <AdminProjects
              projects={projects}
              availableProjects={subscriber?.productPackage?.numberOfProjects || null}
              refreshSubscriber={refreshSubscriber}
            />
          </section>
          <div style={{ marginBottom: '48px' }} />
          <section id={SECTION_IDS.users}>
            <UsersTable
              users={userRows}
              subscriber={subscriber}
              company={company}
              refreshSubscriber={refreshSubscriber}
            />
          </section>
          <div style={{ marginBottom: '48px' }} />
          <section id={SECTION_IDS.securityGroups}>
            <SecurityGroupsPage isForAdminDashboard />
          </section>
          <div style={{ marginBottom: '48px' }} />
          <section id={SECTION_IDS.defaultFolders}>
            <FolderStructures subscriberId={subscriberId} />
          </section>
          <div style={{ marginBottom: '48px' }} />
          <section id={SECTION_IDS.actionTakenLabels}>
            <ActionTakenLabelsPanel subscriberId={subscriberId} />
          </section>
        </>
      );
    }

    return <h2>You must be a subscriber admin to view this page.</h2>;
  };

  return (
    <div>
      <NavAppbar />
      <h1 style={{ color: 'black' }}>Subscriber Admin Dashboard</h1>
      {getContent()}
    </div>
  );
};

export default SubscriberAdminDashboard;
